import { useEffect, useState } from 'react'

import { ESSENTIALS_HOME_CATEGORY_TO_SUB_PATH } from '../../essentials/constants'
import { ESSENTIALS_HOME_CATEGORY } from '../../essentials/types'
import { runEssentialsQuery } from '../../essentials/utils'
import { DEFAULT_LANGUAGE } from '../../intl/constants'
import { ESSENTIALS_GET_MEDIA_ARTICLES } from '../constants'
import {
  GoodnessAnimationImage,
  GoodnessAudio,
  GoodnessImageContent,
  SELF_CARE_MEDIA_TYPE,
  SelfCareMediaContent,
  SelfCareMediaImageContent,
} from '../types'
import { appendHttpsToUrl, parseHtmlTags } from '../utils'

type SelfCareMediaParams = {
  essentialsCmsUrl: string
  slug?: string
  lyraLanguage?: string
  mediaType?: SELF_CARE_MEDIA_TYPE
}

const getImageContent = (images?: GoodnessImageContent[]): SelfCareMediaImageContent | undefined => {
  if (images) {
    for (const image of images) {
      if (image && image.url) {
        const imageUrl = appendHttpsToUrl(image.url)
        const imageAspectRatio = image.width / image.height
        const height = image.height
        const width = image.width
        return { imageUrl, imageAspectRatio, height, width }
      }
    }
  }

  return undefined
}

const getAnimationImageContent = (
  animationImages?: GoodnessAnimationImage[],
): SelfCareMediaImageContent | undefined => {
  if (animationImages) {
    for (const animationImage of animationImages) {
      if (animationImage && animationImage.animated) {
        const animatedImages = animationImage.animated
        if (animationImages) {
          return getImageContent(animatedImages)
        }
      }
    }
  }

  return undefined
}

const parseAudioTranscript = (audioTranscriptHTMLStr?: string) => {
  return audioTranscriptHTMLStr ? parseHtmlTags(audioTranscriptHTMLStr) : ''
}

const getAudioUrl = (audios?: GoodnessAudio[]): string | undefined => {
  if (audios) {
    for (const audio of audios) {
      if (audio && audio.url) {
        return appendHttpsToUrl(audio.url)
      }
    }
  }

  return undefined
}

/**
 * Parse content type into user-friendly display text
 */
const formatContentType = (contentType: string) => {
  return contentType.split('_').join(' ')
}

/**
 * Parse content type into essentials home category to help obtain essentials subPath. Currently, wellness toolkit
 * only has 2 types of content.
 * @param contentTypeStr
 */
const getEssentialsHomeCategory = (contentTypeStr: string): ESSENTIALS_HOME_CATEGORY | undefined => {
  switch (contentTypeStr) {
    case 'meditation':
      return ESSENTIALS_HOME_CATEGORY.MEDITATIONS
    case 'breathing_exercise':
      return ESSENTIALS_HOME_CATEGORY.BREATHING_EXERCISES
  }

  return undefined
}

const getEssentialsCategorySubPath = (category?: ESSENTIALS_HOME_CATEGORY): string | undefined => {
  if (category) {
    return ESSENTIALS_HOME_CATEGORY_TO_SUB_PATH[category]
  }

  return undefined
}

/**
 * Fetch and parse CMS graphQL data from Goodness into SelfCareMediaContent for self care media components.
 *
 * @param essentialsCmsUrl
 * @param lyraLanguage
 * @param slug
 * @param mediaType
 */
export const useGetSelfCareMedia = ({
  essentialsCmsUrl,
  lyraLanguage,
  slug,
  mediaType,
}: SelfCareMediaParams): SelfCareMediaContent | undefined => {
  const [selfCareMedia, setSelfCareMedia] = useState<SelfCareMediaContent | undefined>(undefined)
  const locale = lyraLanguage ?? DEFAULT_LANGUAGE

  useEffect(() => {
    const fetchMedia = async () => {
      if (!essentialsCmsUrl) {
        throw new Error('CMS URL is empty')
      }

      if (!slug) {
        return undefined
      }

      const response = await runEssentialsQuery({
        locale: locale,
        slug: slug,
        query: ESSENTIALS_GET_MEDIA_ARTICLES,
        essentialsCmsUrl: essentialsCmsUrl,
        logError: () => {},
        isSingleResult: true,
      })
      if (response) {
        const imageContent = getImageContent(response?.image)
        let media: SelfCareMediaContent = {
          id: response?.id,
          title: response?.title,
          description: response?.listingExcerpt,
          contentFormat: response?.contentFormat,
          contentType: response?.contentType,
          displayContentType: formatContentType(response?.contentType),
          articleSubPath: response?.uri,
          categorySubPath: getEssentialsCategorySubPath(getEssentialsHomeCategory(response?.contentType)) || '',
          imageContent: imageContent,
          slug: response?.slug,
        }

        if (mediaType === SELF_CARE_MEDIA_TYPE.GIF) {
          const gifImageContent = getAnimationImageContent(response?.animationImages)
          media = {
            ...media,
            gifImageContent: gifImageContent,
          }
        } else if (mediaType === SELF_CARE_MEDIA_TYPE.AUDIO && response?.blocks.length > 2) {
          const audioTranscript = parseAudioTranscript(response?.blocks[2]?.body)
          const audioUrl = getAudioUrl(response?.blocks[0]?.audio) || ''
          const audioContent = {
            audioUrl: audioUrl,
            transcript: audioTranscript,
          }
          media = {
            ...media,
            audioContent: audioContent,
          }
        }

        return media
      }

      return undefined
    }

    fetchMedia().then(setSelfCareMedia)
  }, [essentialsCmsUrl, locale, lyraLanguage, mediaType, slug])

  return selfCareMedia
}
